import React from 'react';

/* ──────────────────────────────────────────────────────────────────
 * The SOV tab's page layout (Duncan 2026-11-03): "the sections just kind of all run together... your eyes don't know
 * what to look at first". Everything stays on one page; each part is set apart and ranked:
 *
 *   Summary                      the tiles and the contract check, on a band of their own
 *   Schedule of Values           the main section: a card with a tinted header (its icon, title, totals, tools)
 *   Where the billing comes from the pay applications read from ComputerEase and the Freeform invoices, side by side
 *
 * A small label names each band, and there's room between them.
 * ────────────────────────────────────────────────────────────────── */

const blue = 'oklch(0.62 0.12 250)';
const tan = 'oklch(0.64 0.1 65)';

/** Each section's color: the icon chip, and its header's tint. */
export const SOV_TONES = {
  sov: { fg: 'var(--accent-text)', chip: 'color-mix(in oklch, var(--accent) 20%, var(--bg-surface))', head: 'color-mix(in oklch, var(--accent) 12%, var(--bg-surface))' },
  apps: { fg: 'oklch(0.5 0.11 250)', chip: `color-mix(in oklch, ${blue} 20%, var(--bg-surface))`, head: `color-mix(in oklch, ${blue} 9%, var(--bg-surface))` },
  freeform: { fg: 'oklch(0.5 0.09 60)', chip: `color-mix(in oklch, ${tan} 22%, var(--bg-surface))`, head: `color-mix(in oklch, ${tan} 10%, var(--bg-surface))` },
};

const PATHS = {
  table: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M3 15h18M9 4v16" /></>,
  invoice: <><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2Z" /><path d="M9 13h6M9 17h4" /></>,
  receipt: <><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16l-3-2-2 2-2-2-2 2-2-2-3 2Z" /><path d="M9 7h6M9 11h6M9 15h4" /></>,
};

/** A section's icon in its colored chip. */
export const SovIcon = ({ tone, icon, size = 30 }) => {
  const t = SOV_TONES[tone];
  return (
    <span aria-hidden="true" style={{
      width: size, height: size, borderRadius: 8, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', color: t.fg, background: t.chip,
    }}>
      <svg width={Math.round(size * 0.56)} height={Math.round(size * 0.56)} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{PATHS[icon]}</svg>
    </span>
  );
};

const LABEL = {
  fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', margin: '0 0 8px 2px',
};

/** A band's name, over it. */
export const SovBandLabel = ({ children }) => <div style={LABEL}>{children}</div>;

/* A band left with nothing in it (no tiles yet, no contract check) takes no room. */
const EMPTY_CSS = '.sov-band:has(> .sov-band-body:empty){display:none}';

/** The Summary band: what's inside it sits on a recessed panel, apart from the sections below. */
export const SovBand = ({ label, children }) => (
  <div className="sov-band" style={{ marginBottom: 28 }}>
    <style>{EMPTY_CSS}</style>
    {label && <SovBandLabel>{label}</SovBandLabel>}
    <div className="sov-band-body" style={{
      display: 'flex', flexDirection: 'column', gap: 8, padding: 8, borderRadius: 14,
      background: 'color-mix(in oklch, var(--text-1) 4%, var(--bg-page))', border: '1px solid var(--border-subtle)',
    }}>{children}</div>
  </div>
);

/* The Summary's slim strips (Duncan 2026-11-03: the 8 boxes "sure take up a lot of space") -- one strip of cells, the
   job top bar's look: a small label over the figure, its note inline after it. */
export const sovStripStyle = {
  display: 'flex', alignItems: 'center', minWidth: 0, padding: '8px 6px', borderRadius: 10, overflowX: 'auto',
  background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-sm)',
};
const CELL_LABEL = {
  fontSize: 11, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap',
};
/** One figure in a strip; `sub` follows it on the same line (cut short, in full on hover). */
export const SovStripCell = ({ label, value, valueColor, sub, subTitle, children }) => (
  <div style={{ flex: '1 1 0', minWidth: 'max-content', padding: '0 14px' }}>
    <div style={CELL_LABEL}>{label}</div>
    {children || (
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap', marginTop: 1 }}>
        <span className="mono" style={{ fontSize: 15, fontWeight: 600, color: valueColor || 'var(--text-1)', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        {sub && (
          <span title={subTitle || (typeof sub === 'string' ? sub : undefined)} style={{
            fontSize: 12, color: 'var(--text-3)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{sub}</span>
        )}
      </div>
    )}
  </div>
);
export const SovStripDivider = () => <span aria-hidden="true" style={{ width: 1, height: 28, flex: '0 0 auto', background: 'var(--border-subtle)' }} />;
export const SovStripOp = ({ children }) => (
  <span aria-hidden="true" style={{ flex: '0 0 auto', color: 'var(--text-3)', fontSize: 16, padding: '0 2px' }}>{children}</span>
);

/** A section card: a header tinted in its color, then its body. `main` is the Schedule of Values -- the one to read. */
export const sovCardStyle = (main) => ({
  border: `1px solid ${main ? 'var(--border-default)' : 'var(--border-subtle)'}`, borderRadius: 14,
  background: 'var(--bg-surface)', boxShadow: main ? 'var(--shadow-md)' : 'var(--shadow-sm)', minWidth: 0,
});
export const sovHeadStyle = (tone) => ({
  padding: '12px 16px', background: SOV_TONES[tone].head, borderBottom: '1px solid var(--border-default)',
  borderRadius: '14px 14px 0 0',
});
